import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const INSTRUMENTS = ["Piano", "Guitar", "Drums", "Voice"];
const EMPTY = { school_name: "", contact_name: "", email: "", classes: 2, instruments: [], message: "" };

export const SchoolEnquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (i) => setF({ ...f, instruments: f.instruments.includes(i) ? f.instruments.filter((x) => x !== i) : [...f.instruments, i] });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/school-enquiry", { ...f, classes: Number(f.classes) || 1 });
      setDone(true);
      setF(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your request");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-8 flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 text-sm" data-testid="school-enquiry-success">
        <CheckCircle2 className="text-emerald-500" size={20} />
        Thanks, your quote request has been sent to Jason. You'll hear back by email soon.
      </div>
    );
  }

  const field = "mt-1 border-border bg-background";
  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl border border-border bg-background p-6" data-testid="school-enquiry-form">
      <p className="font-serif text-xl">Request a quote for several classes</p>
      <p className="mt-1 text-sm text-muted-foreground">Licensing more than one class? Tell Jason what you need and he'll get back to you.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div><Label className="text-muted-foreground">School name</Label><Input required value={f.school_name} onChange={set("school_name")} maxLength={120} data-testid="school-enquiry-school" className={field} /></div>
        <div><Label className="text-muted-foreground">Your name</Label><Input required value={f.contact_name} onChange={set("contact_name")} maxLength={80} data-testid="school-enquiry-name" className={field} /></div>
        <div><Label className="text-muted-foreground">School email</Label><Input required type="email" value={f.email} onChange={set("email")} placeholder="you@yourschool.org" data-testid="school-enquiry-email" className={field} /></div>
        <div><Label className="text-muted-foreground">Number of classes</Label><Input required type="number" min={1} max={100} value={f.classes} onChange={set("classes")} data-testid="school-enquiry-classes" className={field} /></div>
      </div>
      <Label className="mt-4 block text-muted-foreground">Instruments</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {INSTRUMENTS.map((i) => (
          <button key={i} type="button" onClick={() => toggle(i)} data-testid={`school-enquiry-instrument-${i.toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${f.instruments.includes(i) ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>
            {i}
          </button>
        ))}
      </div>
      <Label className="mt-4 block text-muted-foreground">Message (optional)</Label>
      <textarea value={f.message} onChange={set("message")} rows={3} maxLength={2000} data-testid="school-enquiry-message"
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" placeholder="Year groups, start date, anything else…" />
      <Button type="submit" disabled={busy} data-testid="school-enquiry-submit" className="mt-4 rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90">
        <Send size={15} className="mr-2" /> {busy ? "Sending…" : "Request a quote"}
      </Button>
    </form>
  );
};
